import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { COMPANY, SERVICES, SOLUTIONS } from "@/data/site";

const NavItem = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="font-head uppercase tracking-wide text-[15px] font-600 text-[#0B192C] hover:text-[#FF5A00] transition-colors">
    {children}
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); setOpenMenu(null); }, [location.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-[box-shadow,border-color] duration-300 bg-white ${scrolled ? "border-b-2 border-[#0B192C] shadow-sm" : "border-b border-[#0B192C]/10"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 bg-[#FF5A00] flex items-center justify-center">
              <span className="font-head font-black text-white text-2xl leading-none mt-1">C</span>
            </div>
            <div className="leading-none">
              <div className="font-head font-black text-xl tracking-tight text-[#0B192C]">CARGOWYN</div>
              <div className="text-[9px] tracking-[0.35em] text-[#475569] font-semibold uppercase">Logistics Group</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" onMouseLeave={() => setOpenMenu(null)}>
            <div className="relative" onMouseEnter={() => setOpenMenu("services")}>
              <button data-testid="nav-services" className="flex items-center gap-1 font-head uppercase tracking-wide text-[15px] font-600 text-[#0B192C] hover:text-[#FF5A00] transition-colors">
                Services <ChevronDown size={15} className={`transition-transform ${openMenu === "services" ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {openMenu === "services" && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 pt-4 w-72">
                    <div className="bg-white border-2 border-[#0B192C] hard-shadow">
                      {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}
                          className="flex items-center justify-between px-5 py-3 border-b border-[#0B192C]/10 last:border-0 hover:bg-[#0B192C] hover:text-white transition-colors group">
                          <span className="font-head uppercase tracking-wide text-sm font-600">{s.title}</span>
                          <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 text-[#FF5A00]" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative" onMouseEnter={() => setOpenMenu("solutions")}>
              <button data-testid="nav-solutions" className="flex items-center gap-1 font-head uppercase tracking-wide text-[15px] font-600 text-[#0B192C] hover:text-[#FF5A00] transition-colors">
                Solutions <ChevronDown size={15} className={`transition-transform ${openMenu === "solutions" ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {openMenu === "solutions" && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 pt-4 w-72">
                    <div className="bg-white border-2 border-[#0B192C] hard-shadow">
                      {SOLUTIONS.map((s) => (
                        <Link key={s.title} to="/services" data-testid={`nav-solution-${s.title}`}
                          className="flex items-center justify-between px-5 py-3 border-b border-[#0B192C]/10 last:border-0 hover:bg-[#0B192C] hover:text-white transition-colors group">
                          <span className="font-head uppercase tracking-wide text-sm font-600">{s.title}</span>
                          <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 text-[#FF5A00]" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavItem to="/services" testId="nav-locations">Locations</NavItem>
            <NavItem to="/about" testId="nav-about">About Us</NavItem>
            <NavItem to="/contact" testId="nav-contact">Contact</NavItem>
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a href={COMPANY.phoneHref} data-testid="nav-phone" className="flex items-center gap-2 text-[#0B192C] hover:text-[#FF5A00] transition-colors">
              <Phone size={16} /><span className="font-head font-700 tracking-wide">{COMPANY.phone}</span>
            </a>
            <Link to="/quote" data-testid="nav-quote-btn" className="bg-[#FF5A00] text-white font-head font-700 uppercase tracking-[0.15em] text-sm px-6 py-3 hover:bg-[#E65100] transition-colors">
              Get a Quote
            </Link>
          </div>

          <button data-testid="mobile-menu-btn" className="lg:hidden text-[#0B192C]" onClick={() => setMobile(!mobile)}>
            {mobile ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden bg-white border-t border-[#0B192C]/10">
            <div className="px-5 py-6 flex flex-col gap-1">
              <div className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-bold py-2">Services</div>
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="font-head uppercase tracking-wide py-2 border-b border-[#0B192C]/5" data-testid={`m-service-${s.slug}`}>{s.title}</Link>
              ))}
              <Link to="/services" className="font-head uppercase tracking-wide py-3" data-testid="m-locations">Locations</Link>
              <Link to="/about" className="font-head uppercase tracking-wide py-3" data-testid="m-about">About Us</Link>
              <Link to="/contact" className="font-head uppercase tracking-wide py-3" data-testid="m-contact">Contact</Link>
              <a href={COMPANY.phoneHref} className="font-head uppercase tracking-wide py-3 text-[#FF5A00]">{COMPANY.phone}</a>
              <Link to="/quote" className="bg-[#FF5A00] text-white text-center font-head font-700 uppercase tracking-[0.15em] px-6 py-4 mt-2" data-testid="m-quote-btn">Get a Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
